// src/contexts/SquadAuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/lib/supabaseClient';

interface SquadMember {
  id?: string;
  name: string;
  role: string;
  email: string;
  year_of_study?: string;
  course?: string;
  assessment_completed?: boolean;
}

export interface SquadUser {
  id: string;
  squadName: string;      // Squad name (e.g., "Demo Squad Alpha")
  memberName: string;     // Individual member's name (e.g., "John Doe")
  memberRole: string;     // Member's role in the squad
  memberEmail: string;    // Member's email
  code: string;
  university: string;
  phase: string;
  startDate: string;
  members: SquadMember[];
  squadId: string;
  memberId: string;
}

interface SquadAuthState {
  squadUser: SquadUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (squadCode: string, email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

const SquadAuthContext = createContext<SquadAuthState | null>(null);

export function useSquadAuth() {
  const context = useContext(SquadAuthContext);
  if (!context) {
    throw new Error('useSquadAuth must be used within a SquadAuthProvider');
  }
  return context;
}

export function SquadAuthProvider({ children }: { children: ReactNode }) {
  const [squadUser, setSquadUser] = useState<SquadUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Get user profile
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profile && profile.squad_id) {
          // Get squad details
          const { data: squad } = await supabase
            .from('squad_registrations')
            .select('*')
            .eq('id', profile.squad_id)
            .single();

          if (squad) {
            // Get all squad members
            const { data: members } = await supabase
              .from('squad_members')
              .select('*')
              .eq('squad_id', squad.id);

            // Find the current member by email
            const currentMember = members?.find(m => m.email === user.email);

            const squadUserData: SquadUser = {
              id: squad.id,
              squadName: squad.squad_name,
              memberName: currentMember?.full_name || user.email?.split('@')[0] || 'Member',
              memberRole: currentMember?.role || profile.role || 'squad_member',
              memberEmail: user.email || '',
              code: squad.squad_code,
              university: squad.university || '',
              phase: 'problem_selection',
              startDate: new Date().toISOString().split('T')[0],
              members: members?.map(m => ({
                id: m.id,
                name: m.full_name,
                role: m.role,
                email: m.email,
                year_of_study: m.year_of_study,
                course: m.course,
                assessment_completed: m.assessment_completed
              })) || [],
              squadId: squad.id,
              memberId: currentMember?.id || profile.id,
            };

            setSquadUser(squadUserData);
          }
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (squadCode: string, email: string, password: string): Promise<boolean> => {
    try {
      console.log('🔐 Login attempt:', { squadCode, email });
      
      // Step 1: Verify squad exists
      const { data: squad, error: squadError } = await supabase
        .from('squad_registrations')
        .select('*')
        .eq('squad_code', squadCode)
        .single();

      if (squadError || !squad) {
        console.error('❌ Squad not found');
        return false;
      }

      // Step 2: Verify member exists in this squad
      const { data: member, error: memberError } = await supabase
        .from('squad_members')
        .select('*')
        .eq('squad_id', squad.id)
        .eq('email', email)
        .single();

      if (memberError || !member) {
        console.error('❌ Member not found in this squad');
        return false;
      }

      // Step 3: Authenticate with Supabase
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (authError) {
        console.error('❌ Auth error:', authError.message);
        return false;
      }

      // Step 4: Update or create user profile with squad_id
      const { error: profileError } = await supabase
        .from('user_profiles')
        .upsert({
          id: authData.user.id,
          email: email,
          full_name: member.full_name,
          role: member.role,
          squad_id: squad.id,
          updated_at: new Date().toISOString()
        });

      if (profileError) {
        console.error('❌ Profile error:', profileError);
      }

      // Step 5: Reload user data
      await checkUser();
      console.log('✅ Login successful!');
      return true;

    } catch (error) {
      console.error('❌ Login error:', error);
      return false;
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setSquadUser(null);
  };

  return (
    <SquadAuthContext.Provider value={{
      squadUser,
      isAuthenticated: !!squadUser,
      isLoading,
      login,
      logout
    }}>
      {children}
    </SquadAuthContext.Provider>
  );
}